// src/components/ProductCard.js
import React, { useState } from 'react';

const ProductCard = ({ 
  id, 
  product, 
  onClick, 
  primaryColor, 
  secondaryColor,
  language = 'en', // 'en' or 'km',
  onAddToCart // New prop for add to cart
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  // Get image URL with proper error handling
  const getImageUrl = () => {
    try {
      if (!product) return '';
      
      const images = product.images;
      if (images && Array.isArray(images) && images.length > 0) {
        const imagePath = images[0];
        if (!imagePath) return '';
        if (imagePath.startsWith('http')) return imagePath;
        if (imagePath.startsWith('data:')) return imagePath;
        return `https://easymenu-backend-4w2d.onrender.com${imagePath}`;
      }
      return '';
    } catch (error) {
      console.error('Error loading image:', error);
      return '';
    }
  };

  // Calculate discount with safety check
  const calculateDiscount = () => {
    try {
      if (!product) return 0;
      const price = parseFloat(product.price) || 0;
      const discountPrice = parseFloat(product.discount_price) || 0;
      if (discountPrice > 0 && price > 0 && discountPrice < price) {
        return Math.round(((price - discountPrice) / price) * 100);
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  // Translations
  const translations = {
    en: {
      viewDetail: 'View Detail',
      noImage: 'No Image',
      addToCart: 'Add to Cart',
      added: 'Added!'
    },
    km: {
      viewDetail: 'មើលព័ត៌មានលម្អិត',
      noImage: 'គ្មានរូបភាព',
      addToCart: 'កម្ម៉ង់',
      added: 'បានបន្ថែម!'
    }
  };

  const t = translations[language] || translations.en;

  // Safety check - if product is undefined or null
  if (!product) {
    return null;
  }

  const imageUrl = getImageUrl();
  const discount = calculateDiscount();
  const title = product.title || 'Untitled Product';
  const price = parseFloat(product.price) || 0;
  const discountPrice = parseFloat(product.discount_price) || 0;
  const hasDiscount = discountPrice > 0 && discountPrice < price;

  // Handle click with safety
  const handleClick = (e) => {
    if (onClick) {
      onClick();
    }
  };

  // Handle add to cart
  const handleAddToCart = (e) => {
    e.stopPropagation();
    
    if (onAddToCart) {
      setIsAdding(true);
      onAddToCart(product);
      
      // Show feedback
      setShowFeedback(true);
      setTimeout(() => {
        setIsAdding(false);
        setTimeout(() => {
          setShowFeedback(false);
        }, 1500);
      }, 500);
    }
  };

  return (
    <div 
      id={id}
      className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer group border border-gray-100/50 hover:border-transparent flex flex-col h-full overflow-hidden"
      onClick={handleClick}
      style={{ fontFamily: getFontFamily() }}
    >
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img 
          src={imageUrl} 
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            e.target.src = '';
          }}
        />
        
        {/* Discount Badge */}
        {hasDiscount && discount > 0 && (
          <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
            {discount}% OFF
          </div>
        )}
        
        {/* Quick view overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
          <button 
            className="bg-white/90 backdrop-blur-sm text-gray-800 px-4 py-2 rounded-full text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-100 scale-95 shadow-lg"
            style={{ 
              fontFamily: getFontFamily(),
              color: primaryColor || '#3B82F6'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {t.viewDetail}
          </button>
        </div>
      </div>
      
      <div className="p-3 md:p-4 flex-1 flex flex-col">
        <h3 
          className="text-sm md:text-base font-medium text-gray-800 mb-1 line-clamp-2 leading-tight hover:text-blue-600 transition-colors"
          style={{ fontFamily: getFontFamily() }}
        >
          {title}
        </h3>
        
        <div className="mt-auto">
          <div className="flex items-baseline gap-2 mb-3">
            {hasDiscount ? (
              <>
                <span className="text-lg md:text-xl font-bold" style={{ color: primaryColor || '#3B82F6', fontFamily: getFontFamily() }}>
                  ${discountPrice.toFixed(2)}
                </span>
                <span className="text-gray-400 line-through text-xs md:text-sm">
                  ${price.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="text-lg md:text-xl font-bold" style={{ color: primaryColor || '#3B82F6', fontFamily: getFontFamily() }}>
                ${price.toFixed(2)}
              </span>
            )}
          </div>
          
          <div className="flex gap-2">
            {/* Add to Cart Button */}
            <button 
              className="flex-1 py-2.5 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md"
              style={{ 
                backgroundColor: primaryColor || '#3B82F6',
                fontFamily: getFontFamily()
              }}
              onClick={handleAddToCart}
            >
              {isAdding ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t.added}
                </span>
              ) : showFeedback ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-check-circle"></i>
                  {t.added}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-cart-plus"></i>
                  {t.addToCart}
                </span>
              )}
            </button>

            {/* View Detail Button */}
            <button 
              className="py-2.5 px-3 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:opacity-90"
              style={{ 
                backgroundColor: secondaryColor || primaryColor || '#10B981',
                fontFamily: getFontFamily()
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              <i className="fas fa-eye"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;